import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { BrowserProvider, formatEther } from "ethers";
import { toast } from "sonner";
import api from "@/lib/api";

import { CHAIN } from "@/contracts/croc";

const WalletContext = createContext(null);
export const useWallet = () => useContext(WalletContext);

// Dynamic network definition derived from deployment (supports both BSC Mainnet 56 and Testnet 97)
const TARGET_CHAIN = {
  chainId: "0x" + Number(CHAIN.id || 56).toString(16),
  chainName: CHAIN.name || "BNB Smart Chain",
  nativeCurrency: { name: "BNB", symbol: "BNB", decimals: 18 },
  rpcUrls: [CHAIN.rpc || "https://bsc-dataseed.bnbchain.org"],
  blockExplorerUrls: [CHAIN.explorer || "https://bscscan.com"],
};
const NETWORKS = { 56: "BSC Mainnet", 97: "BSC Testnet", 1: "Ethereum", 137: "Polygon" };

const createWcProvider = async () => {
  const projectId = process.env.REACT_APP_WALLETCONNECT_PROJECT_ID;
  const { EthereumProvider } = await import("@walletconnect/ethereum-provider");
  const targetId = Number(CHAIN.id || 56);
  return EthereumProvider.init({
    projectId,
    chains: [targetId],
    optionalChains: targetId === 56 ? [97] : [56],
    showQrModal: true,
    rpcMap: {
      56: "https://bsc-dataseed.binance.org",
      97: "https://data-seed-prebsc-1-s1.bnbchain.org:8545",
    },
    metadata: {
      name: "CROC PROTOCOL",
      description: "Apex DeFi staking on BNB Chain",
      url: window.location.origin,
      icons: [window.location.origin + "/logo.png"],
    },
  });
};

export function WalletProvider({ children }) {
  const [address, setAddress] = useState(null);
  const [chainId, setChainId] = useState(null);
  const [balance, setBalance] = useState(null);
  const [user, setUser] = useState(null);
  const [connecting, setConnecting] = useState(false);
  const [ready, setReady] = useState(false);
  const [walletType, setWalletType] = useState(null);
  const eipRef = useRef(null); // active EIP-1193 provider (injected or walletconnect)
  const wcRef = useRef(null);

  const loadUser = useCallback(async (addr) => {
    try {
      const ref = localStorage.getItem("croc_ref") || (localStorage.getItem("croc_demo") === "1" ? "CROCOP1" : null);
      try {
        await api.connect(addr, ref);
      } catch (e) {
        const detail = e?.response?.data?.detail;
        // The referral code is a one-time join token: once the wallet is registered, any
        // re-registration attempt is rejected server-side — that's expected for a returning
        // user whose stale code is still in localStorage. Clear it and continue to load.
        if (detail && /already registered/i.test(detail)) {
          localStorage.removeItem("croc_ref");
        } else {
          throw e;
        }
      }
      if (localStorage.getItem("croc_ref")) localStorage.removeItem("croc_ref");
      const u = await api.user(addr);
      setUser(u);
      return u;
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail && /referral code is required/i.test(detail)) {
        toast.error("Referral required", {
          description: "You need an invite link from an existing member to join CROC PROTOCOL.",
        });
        return { blocked: true };
      }
      console.error(e);
    }
  }, []);

  const refreshUser = useCallback(async () => {
    if (address) return loadUser(address);
  }, [address, loadUser]);

  const finalizeConnect = useCallback(
    async (addr, eip, type = "injected") => {
      const eth = eip || window.ethereum;
      eipRef.current = eth;
      const provider = new BrowserProvider(eth);
      const net = await provider.getNetwork();
      const bal = await provider.getBalance(addr);
      setAddress(addr.toLowerCase());
      setChainId(Number(net.chainId));
      setBalance(parseFloat(formatEther(bal)).toFixed(4));
      setWalletType(type);
      localStorage.setItem("croc_addr", addr.toLowerCase());
      localStorage.removeItem("croc_demo");
      const res = await loadUser(addr.toLowerCase());
      if (res?.blocked) { disconnectRef.current?.(); return; }
    },
    [loadUser]
  );

  const connect = useCallback(
    async (walletLabel = "MetaMask") => {
      const eth = window.ethereum;
      if (!eth) {
        toast.error("No injected wallet", {
          description: "Install MetaMask, or use WalletConnect for mobile wallets.",
        });
        return;
      }
      try {
        setConnecting(true);
        const accounts = await eth.request({ method: "eth_requestAccounts" });
        if (!accounts?.length) return;
        try {
          await eth.request({ method: "wallet_switchEthereumChain", params: [{ chainId: TARGET_CHAIN.chainId }] });
        } catch (sw) {
          if (sw.code === 4902) {
            try { await eth.request({ method: "wallet_addEthereumChain", params: [TARGET_CHAIN] }); } catch (_) {}
          }
        }
        await finalizeConnect(accounts[0], eth, "injected");
        toast.success(`${walletLabel} connected`, { description: "Welcome to the apex, predator." });
      } catch (e) {
        console.error("Wallet connect error:", e);
        if (e?.code === 4001) {
          toast.error("Connection rejected by user");
        } else {
          toast.error("Connection failed", { description: e?.message || "Check your wallet extension" });
        }
      } finally {
        setConnecting(false);
      }
    },
    [finalizeConnect]
  );

  const connectWalletConnect = useCallback(async () => {
    if (!process.env.REACT_APP_WALLETCONNECT_PROJECT_ID) return toast.error("WalletConnect not configured");
    try {
      setConnecting(true);
      const wc = await createWcProvider();
      wc.on("disconnect", () => disconnectRef.current?.());
      wc.on("accountsChanged", (a) =>
        a?.length ? finalizeConnect(a[0], wc, "walletconnect") : disconnectRef.current?.()
      );
      await wc.enable();
      wcRef.current = wc;
      localStorage.setItem("croc_wc", "1");
      if (wc.accounts?.length) {
        await finalizeConnect(wc.accounts[0], wc, "walletconnect");
        toast.success("WalletConnect linked");
      }
    } catch (e) {
      toast.error("WalletConnect failed", { description: (e?.message || "").slice(0, 80) });
    } finally {
      setConnecting(false);
    }
  }, [finalizeConnect]);

  const connectDemo = useCallback(async () => {
    let addr = localStorage.getItem("croc_demo_addr");
    if (!addr) {
      const hex = "0123456789abcdef";
      addr = "0x" + Array.from({ length: 40 }, () => hex[Math.floor(Math.random() * 16)]).join("");
      localStorage.setItem("croc_demo_addr", addr);
    }
    setAddress(addr);
    setChainId(97);
    setBalance("12.5000");
    setWalletType("demo");
    localStorage.setItem("croc_addr", addr);
    localStorage.setItem("croc_demo", "1");
    await loadUser(addr);
    toast.success("Demo mode active", { description: "Exploring CROC PROTOCOL with a simulated wallet." });
  }, [loadUser]);

  const disconnect = useCallback(() => {
    try { wcRef.current?.disconnect?.(); } catch (_) {}
    wcRef.current = null;
    eipRef.current = null;
    setAddress(null);
    setUser(null);
    setBalance(null);
    setChainId(null);
    setWalletType(null);
    localStorage.removeItem("croc_addr");
    localStorage.removeItem("croc_demo");
    localStorage.removeItem("croc_wc");
  }, []);
  const disconnectRef = useRef(disconnect);
  disconnectRef.current = disconnect;

  const getBrowserProvider = useCallback(() => {
    if (walletType === "demo" || !eipRef.current) return null;
    return new BrowserProvider(eipRef.current);
  }, [walletType]);

  const ensureTargetNetwork = useCallback(async () => {
    const eth = eipRef.current;
    if (!eth) return false;
    try {
      await eth.request({ method: "wallet_switchEthereumChain", params: [{ chainId: TARGET_CHAIN.chainId }] });
    } catch (sw) {
      if (sw?.code === 4902) {
        try { await eth.request({ method: "wallet_addEthereumChain", params: [TARGET_CHAIN] }); } catch (_) { return false; }
      }
    }
    return true;
  }, []);

  useEffect(() => {
    const eth = window.ethereum;
    const wantsDemo = new URLSearchParams(window.location.search).get("demo") === "1";
    let saved = localStorage.getItem("croc_addr");
    let isDemo = localStorage.getItem("croc_demo") === "1";
    if (wantsDemo) {
      if (!saved) {
        const hex = "0123456789abcdef";
        saved = "0x" + Array.from({ length: 40 }, () => hex[Math.floor(Math.random() * 16)]).join("");
      }
      localStorage.setItem("croc_addr", saved);
      localStorage.setItem("croc_demo", "1");
      isDemo = true;
    }
    if (isDemo && saved) {
      setAddress(saved);
      setChainId(97);
      setBalance("12.5000");
      setWalletType("demo");
      setReady(true);
      loadUser(saved);
      return;
    }
    const restore = async () => {
      if (localStorage.getItem("croc_wc") === "1" && saved) {
        try {
          const wc = await createWcProvider();
          wc.on("disconnect", () => disconnectRef.current?.());
          wc.on("accountsChanged", (a) =>
            a?.length ? finalizeConnect(a[0], wc, "walletconnect") : disconnectRef.current?.()
          );
          if (wc.accounts?.length) {
            wcRef.current = wc;
            const pick = wc.accounts.find((a) => a.toLowerCase() === saved) || wc.accounts[0];
            await finalizeConnect(pick, wc, "walletconnect");
            return;
          }
          try { await wc.disconnect(); } catch (_) {}
        } catch (_) {}
        localStorage.removeItem("croc_wc");
      }
      if (eth && saved) {
        try {
          const accts = await eth.request({ method: "eth_accounts" });
          if (accts?.length) {
            const pick = accts.find((a) => a.toLowerCase() === saved) || accts[0];
            await finalizeConnect(pick, eth, "injected");
          }
        } catch (_) {}
      }
    };
    restore().finally(() => setReady(true));
    if (eth) {
      const onAccts = (accts) =>
        localStorage.getItem("croc_wc") === "1" ? null : accts?.length ? finalizeConnect(accts[0], eth, "injected") : disconnectRef.current();
      const onChain = () => window.location.reload();
      eth.on?.("accountsChanged", onAccts);
      eth.on?.("chainChanged", onChain);
      return () => {
        eth.removeListener?.("accountsChanged", onAccts);
        eth.removeListener?.("chainChanged", onChain);
      };
    }
  }, [finalizeConnect, loadUser]);

  const value = {
    address,
    chainId,
    balance,
    user,
    connecting,
    walletType,
    isDemo: walletType === "demo",
    connect,
    connectWalletConnect,
    connectDemo,
    disconnect,
    refreshUser,
    getBrowserProvider,
    ensureTestnet: ensureTargetNetwork,
    ensureNetwork: ensureTargetNetwork,
    ready,
    networkName: NETWORKS[chainId] || (chainId ? `Chain ${chainId}` : "—"),
    isBSC: chainId === 56 || chainId === 97,
    isTargetChain: chainId === (CHAIN.id || 56),
    isTestnet: chainId === 97,
  };
  return <WalletContext.Provider value={value}>{children}</WalletContext.Provider>;
}
